import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {StoreDetailResponseItem} from '../../types/store/storeDetails'
import {useEffect, useRef, useState} from 'react'

const markerAssetsMap = {
  active: getAssetUrl('static/img/google-map-markers/marker-active.png'),
  default: getAssetUrl('static/img/google-map-markers/marker.png'),
  storeDetail: getAssetUrl('static/img/google-map-markers/marker-store-details.png'),
} as const

export interface MarkerProps {
  onMarkerClick?: (store: StoreDetailResponseItem) => void
  selectedMarkerId: string
  storeDetail: StoreDetailResponseItem
  options: google.maps.MarkerOptions
  map?: google.maps.Map
  num: number
  setLastMarkerPlaced: () => void
  isStoreDetailsMarker?: boolean
}

export const Marker = ({
  storeDetail,
  onMarkerClick,
  selectedMarkerId,
  map,
  options,
  num,
  setLastMarkerPlaced,
  isStoreDetailsMarker = false,
}: MarkerProps) => {
  const [marker, setMarker] = useState<google.maps.Marker>()
  const infoWindow = useRef<google.maps.InfoWindow | null>(null)

  const isSelected = selectedMarkerId === storeDetail?.id

  const encodeAddress = () => {
    return encodeURIComponent(
      [storeDetail.address1, storeDetail.city, storeDetail.postalCode].filter(Boolean).join(', ')
    )
  }

  const applyValuesToInfoWindowTemplate = (storeDetail: StoreDetailResponseItem) => {
    return `<div class="info-window-content">
               <p>${storeDetail.name}</p>
               <p>${storeDetail.address1}</p>
               <p>${storeDetail.address2}</p>
               <p>${storeDetail.city}</p>
               <p>${storeDetail.postalCode}</p>
               <a class="get-directions" href="https://maps.google.com/maps?daddr=${encodeAddress()}">Get directions</a>
            </div>`
  }

  const buildInfoWindow = () => {
    const content = applyValuesToInfoWindowTemplate(storeDetail)
    infoWindow.current = new google.maps.InfoWindow({
      content: content,
      position: marker?.getPosition(),
      maxWidth: 265,
      pixelOffset: new google.maps.Size(0, -5, 'px', 'px'),
    })
  }

  useEffect(() => {
    if (!marker) {
      setMarker(new window.google.maps.Marker())
      buildInfoWindow()
    }

    return () => {
      if (marker) {
        marker.setMap(null)
      }
    }
  }, [marker])

  useEffect(() => {
    if (!marker || !map) return

    if (!isSelected) {
      infoWindow?.current?.close()
    }

    const opts: google.maps.MarkerOptions = {
      ...options,
      clickable: true,
      title: storeDetail.name,
      map: map,
      label: !isStoreDetailsMarker
        ? {
            text: String(num),
            color: 'white',
            className: 'store-map-marker',
          }
        : undefined,
      icon: {
        url: isStoreDetailsMarker
          ? markerAssetsMap['storeDetail']
          : isSelected
          ? markerAssetsMap['active']
          : markerAssetsMap['default'],
        scaledSize: new google.maps.Size(40, 40),
      },
    }

    marker.setOptions(opts)

    marker.addListener('click', () => {
      infoWindow?.current?.open({map: map, anchor: marker})
      onMarkerClick?.(storeDetail)
    })

    setLastMarkerPlaced()
  }, [marker, options, isSelected, map])

  return null
}
